import { siqBaseUrl } from "@/constants";
import { EmeraldButton } from "@emerald-react/button";
import { EmptyState } from "@emerald-react/empty-state";
import { useNetworkState } from "@shared/hooks/useNetworkState";
import { StorageUtil } from "@shared/utils";
import { useNavigate } from "react-router";

type NotFoundPageProps = {
  title?: string;
  description?: string;
};

/**
 * NotFoundPage component displays a centered empty state for 404 errors
 * Can be used for page not found or resource not found scenarios
 */
const NotFoundPage = ({
  title = "Page Not Found",
  description = "The page you are looking for does not exist.",
}: NotFoundPageProps) => {
  const navigate = useNavigate();
  const isOnline = useNetworkState();

  const goHome = () => {
    // redirect user to the SIQ when appMode is surveyBuilderOnly
    const appMode = StorageUtil.getSessionItem("appMode");
    const sc_surveyId = StorageUtil.getSessionItem("sc_surveyId");
    if (
      appMode &&
      appMode === "surveyBuilderOnly" &&
      sc_surveyId &&
      siqBaseUrl
    ) {
      const siq_project = StorageUtil.getSessionItem("siq_project");
      // Validate project to safe alphanumeric slug; reject any value with path chars
      const safeProject =
        siq_project && siq_project !== "_" && /^[\w-]+$/.test(siq_project)
          ? encodeURIComponent(siq_project)
          : null;
      const destination = safeProject
        ? `${siqBaseUrl}projects/${safeProject}/deliverables`
        : siqBaseUrl;
      window.location.assign(destination);
    } else {
      navigate("/");
    }
  };

  const refreshPage = () => {
    window.location.reload();
  };

  const displayTitle = !isOnline ? "Check your network connection" : title;
  const displayDescription = !isOnline
    ? "Please ensure you are connected to the internet and try again."
    : description;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center">
      <EmptyState
        title={displayTitle}
        description={displayDescription}
        position="center"
        status={!isOnline ? "connection_lost" : "no_results"}
      />
      <div className="flex gap-3 mt-4">
        <EmeraldButton onClick={goHome}>Go to Home</EmeraldButton>
        <EmeraldButton onClick={refreshPage} type="Secondary">
          Refresh Page
        </EmeraldButton>
      </div>
    </div>
  );
};

export default NotFoundPage;
